import { action, makeObservable, observable, runInAction } from 'mobx';
import { ILookup, LookupValue, LookupValueId, LookupValueSelectListener } from './ILookup';
import { ICompositeProductApi } from '@canvas-logic/clp-sdk';
import {
  getAttributeValue,
  ICfgAttributeValue,
  ICfgAttributeValueValuable,
  IComponentProductData,
  VariantProperties,
} from '@canvas-logic/clp-domain';

let _id = 0;

// TODO: rewrite
export class ProductLookup implements ILookup<ICfgAttributeValueValuable<string>> {
  public readonly id = 'attribute-lookup-' + ++_id;
  values: LookupValue<ICfgAttributeValueValuable<string>>[] = [];
  current: LookupValue<ICfgAttributeValueValuable<string>> | null = null;
  loading = false;
  initialized = false;
  private valueSelectListener?: LookupValueSelectListener<ICfgAttributeValueValuable<string>>;
  productData: IComponentProductData[] = [];
  private _enable = false;
  private _canBeDisable = false;

  constructor(private readonly api: ICompositeProductApi, public readonly componentKey: string) {
    makeObservable(this, {
      select: action.bound,
      unselect: action.bound,
      updateValues: action.bound,
      updateCurrent: action.bound,
      values: observable,
      loading: observable,
      current: observable,
      initialized: observable,
    });
    const initialValue = api.getAttributeValue(this.componentKey, VariantProperties.productId);
    if (initialValue === undefined) {
      this.values = [];
    } else {
      this.values = [
        {
          id: '0',
          value: initialValue,
        },
      ];
    }

    this.productData = this.api.getComponentAllProducts(this.componentKey);
    this._enable = this.api.isEnable(this.componentKey);
    this.updateValues();
  }

  isEnable() {
    return this._enable;
  }

  get canBeDisable(): boolean {
    return this._canBeDisable;
  }

  get componentVariant() {
    return this.api.getComponentVariant(this.componentKey);
  }

  unselect() {
    this.api
      .setAvailability(this.componentKey, false)
      .then(() => this.updateValues())
      .then(() => {
        this.updateCurrent();
        if (this.valueSelectListener) {
          this.valueSelectListener('' as any);
        }
      });
  }

  select(id: ICfgAttributeValueValuable<string>) {
    runInAction(() => {
      if (this.api.isAvailable(this.componentKey)) {
        const item = this.values.find((it) => it.value.value === id.value);
        if (item === undefined) {
          throw new Error('Unexpected lookup value id');
        }
        const value = item.value;
        this.api
          .setAttributeValue(this.componentKey, VariantProperties.productId, value)
          .then(() => this.updateValues())
          .then(() => {
            this.updateCurrent();
            if (this.valueSelectListener) {
              this.valueSelectListener(value);
            }
          });
      } else {
        this.api
          .setAvailability(this.componentKey, true)
          .then(() => this.updateValues())
          .then(() => {
            const item = this.values.find((it) => it.value.value === id.value);
            if (item === undefined) {
              throw new Error('Unexpected lookup value id');
            }
            const value = item.value;
            return this.api
              .setAttributeValue(this.componentKey, VariantProperties.productId, value)
              .then(() => this.updateValues())
              .then(() => {
                this.updateCurrent();
                if (this.valueSelectListener) {
                  this.valueSelectListener(value);
                }
              });
          });
      }
    });
  }

  async updateValues() {
    runInAction(async () => {
      this.loading = true;
      const values =
        (await this.api.loadCompatibleAttributeValues(this.componentKey, VariantProperties.productId)) ?? [];
      this.values = values.map((value, payload, index) => ({
        id: index.toString(),
        value: value,
      }));
      this._enable = this.api.isEnable(this.componentKey);
      const availability = await this.api.loadAvailability(this.componentKey);
      this._canBeDisable = availability.some((it) => !it);

      this.updateCurrent();
      this.loading = false;
      runInAction(() => {
        this.initialized = true;
      });
    });
  }

  setValueSelectListener(listener: LookupValueSelectListener<ICfgAttributeValue>) {
    this.valueSelectListener = listener;
  }

  updateCurrent() {
    if (!this._enable) {
      this.current = null;
      return;
    }
    const selectedValue = this.api.getAttributeValue(this.componentKey, VariantProperties.productId);
    if (selectedValue === undefined) {
      this.current = null;
      return;
    }
    const value = getAttributeValue(selectedValue);
    const current = this.values.find((it) => value === getAttributeValue(it.value));
    if (!current) {
      throw new Error(`Unexpected model property value ${value}. Values: ${this.values.join(',')}`);
    }
    this.current = current;
  }
}
